import {
    LogLevel,
    PublicClientApplication,
    type AccountInfo,
    type RedirectRequest,
    type Configuration,
    type EndSessionRequest,
} from '@azure/msal-browser';
import { split } from 'lodash';
import { reactive } from 'vue';

export const loginRequest: RedirectRequest = {
    scopes: split(import.meta.env.VITE_MSAL_AZURE_AD_B2C_APP_SCOPE_URI, ','),
    prompt: 'login',
};

export const msalConfig: Configuration = {
    auth: {
        clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
        authority: import.meta.env.VITE_MSAL_AUTHORITY,
        knownAuthorities: split(import.meta.env.VITE_MSAL_KNOW_AUTHORITIES, ','),
        redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin, // Replace with your actual redirect URI
        postLogoutRedirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin,
        navigateToLoginRequestUrl: true, // If 'true', will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage',
        temporaryCacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
        secureCookies: true,
        cacheMigrationEnabled: true,
        claimsBasedCachingEnabled: true,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) {
                    return;
                }
                if (import.meta.env.VITE_APP_MODE !== 'development') {
                    return;
                }

                const timestamp = new Date().toISOString();
                const prefix = `[${timestamp}] [MSAL]`;

                /* eslint-disable no-console */
                switch (level) {
                    case LogLevel.Error:
                        console.error(`${prefix} ERROR:`, message);
                        return;
                    case LogLevel.Info:
                        console.info(`${prefix} INFO:`, message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(`${prefix} VERBOSE:`, message);
                        return;
                    case LogLevel.Warning:
                        console.warn(`${prefix} WARNING:`, message);
                        return;
                }
            },
            piiLoggingEnabled: false,
            logLevel: LogLevel.Verbose, // Enable verbose logging
        },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: false,
        allowRedirectInIframe: true,
    },
};

export const state = reactive({
    isAuthenticated: false,
    user: null as AccountInfo | null,
});

let msalInstanceObj: PublicClientApplication | null = null;

const appMode = import.meta.env.VITE_APP_MODE;
const deploymentMode = import.meta.env.VITE_APP_DEPLOYMENT_MODE;

// 1. Development mode with local deployment - no MSAL required
if (appMode === 'development' && deploymentMode === 'local') {
    msalInstanceObj = null;
}
// 2. Development mode with development deployment - MSAL required
else if (appMode === 'development' && (deploymentMode === 'development' || deploymentMode === 'cloud')) {
    msalInstanceObj = new PublicClientApplication(msalConfig);
}
// 3. Staging mode with staging deployment - MSAL required
else if (appMode === 'staging' && (deploymentMode === 'staging' || deploymentMode === 'cloud')) {
    msalInstanceObj = new PublicClientApplication(msalConfig);
}
// 4. Production mode with production/prod deployment - MSAL required
else if (appMode === 'production' && (deploymentMode === 'production' || deploymentMode === 'cloud')) {
    msalInstanceObj = new PublicClientApplication(msalConfig);
}
// 5. Any other combination is invalid
else {
    throw new Error(
        `Invalid configuration: VITE_APP_MODE="${appMode}" and VITE_APP_DEPLOYMENT_MODE="${deploymentMode}". ` +
            `Valid combinations are: ` +
            `(development, local), (development, development), (staging, staging), (production, production/prod)`
    );
}

export const msalInstance = msalInstanceObj;

export const logoutRequest: EndSessionRequest = {
    account: null,
    idTokenHint: '',
    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
};
